/**
 * Nome da organização na Base de Conhecimento (TASK-24787). Muda só o primeiro nível
 * da árvore: `organizations.name` continua o mesmo nas outras telas. Vazio volta ao
 * nome da organização. A trilha é `kb.org.rotulo`.
 */
import { useEffect, useState } from 'react'
import { Button, Group, Modal, Stack, Text, TextInput } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { setKbRotulo } from '@/api/kb'
import { mensagemDeErro } from './acesso'

export const ROTULO_MAX = 80

export interface AlvoDoRotulo {
  organizationId: number
  /** `organizations.name`: o que as outras telas mostram. */
  nomeDaOrganizacao: string
  /** O que a árvore mostra hoje. */
  rotuloAtual: string
}

interface Props {
  alvo: AlvoDoRotulo | null
  onClose: () => void
}

export function DialogoDeRotulo({ alvo, onClose }: Props) {
  const qc = useQueryClient()
  const [rotulo, setRotulo] = useState('')

  useEffect(() => {
    if (alvo) setRotulo(alvo.rotuloAtual)
  }, [alvo])

  const salvar = useMutation({
    mutationFn: () => {
      const limpo = rotulo.trim()
      const valor = limpo === '' || limpo === alvo!.nomeDaOrganizacao ? null : limpo
      return setKbRotulo(alvo!.organizationId, valor)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['kb-rotulos'] })
      notifications.show({ color: 'teal', title: 'Nome atualizado', message: rotulo.trim() || alvo!.nomeDaOrganizacao })
      onClose()
    },
    onError: (e) =>
      notifications.show({ color: 'red', title: 'Não foi possível salvar', message: mensagemDeErro(e, 'Falha ao salvar o nome.') }),
  })

  const longo = rotulo.trim().length > ROTULO_MAX

  return (
    <Modal opened={alvo !== null} onClose={onClose} title={<Text fw={700}>Nome na Base de Conhecimento</Text>}>
      <Stack gap="sm">
        <TextInput
          label="Nome"
          value={rotulo}
          onChange={(e) => setRotulo(e.currentTarget.value)}
          error={longo ? `Até ${ROTULO_MAX} caracteres.` : undefined}
          data-autofocus
        />
        <Text size="xs" c="dimmed">
          Muda só o nome nesta tela. Nas outras telas da plataforma a organização continua como {alvo?.nomeDaOrganizacao}.
          Deixe em branco para voltar a esse nome.
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Cancelar</Button>
          <Button loading={salvar.isPending} disabled={longo} onClick={() => salvar.mutate()}>Salvar</Button>
        </Group>
      </Stack>
    </Modal>
  )
}
